import { theme } from "@elyxeer/ui";
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider } from "@mui/material/styles";
import { QueryClientProvider } from "@tanstack/react-query";
import { useEffect } from "react";
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
} from "react-router-dom";

import { AuthGate } from "~/components/auth/AuthGate";
import { AdminShell } from "~/components/shell/AdminShell";
import { I18nProvider } from "~/i18n";
import { trackKeyboardInset } from "~/lib/keyboard-inset";
import { queryClient } from "~/lib/query-client";
import { AnalyticsRoute } from "~/routes/AnalyticsRoute";
import { ArticlesRoute } from "~/routes/ArticlesRoute";
import { ChatRoute } from "~/routes/ChatRoute";
import { DashboardHighlightEditorRoute } from "~/routes/DashboardHighlightEditorRoute";
import { DashboardHighlightsRoute } from "~/routes/DashboardHighlightsRoute";
import { EditorRoute } from "~/routes/EditorRoute";
import { EventEditorRoute } from "~/routes/EventEditorRoute";
import { EventsRoute } from "~/routes/EventsRoute";
import { UserEditorRoute } from "~/routes/UserEditorRoute";
import { UsersRoute } from "~/routes/UsersRoute";

export function App() {
  useEffect(() => trackKeyboardInset(), []);

  return (
    <ThemeProvider theme={theme} defaultMode="system" disableTransitionOnChange>
      <CssBaseline enableColorScheme />
      <I18nProvider>
        <QueryClientProvider client={queryClient}>
          {/*
            Real paths, unlike the member app's hash routing: the panel only ever
            ships as a hosted site, and Firebase Hosting rewrites everything to
            index.html. There is no Capacitor bundle to serve from a filesystem.
          */}
          <Router>
            <AuthGate>
              <Routes>
                <Route element={<AdminShell />}>
                  <Route path="/articles" element={<ArticlesRoute />} />
                  {/* The static segment outranks `:id`, so "new" never reaches the query. */}
                  <Route path="/articles/new" element={<EditorRoute />} />
                  <Route path="/articles/:id" element={<EditorRoute />} />
                  <Route path="/events" element={<EventsRoute />} />
                  <Route path="/events/new" element={<EventEditorRoute />} />
                  <Route path="/events/:id" element={<EventEditorRoute />} />
                  <Route
                    path="/dashboard-highlights"
                    element={<DashboardHighlightsRoute />}
                  />
                  <Route
                    path="/dashboard-highlights/new"
                    element={<DashboardHighlightEditorRoute />}
                  />
                  <Route
                    path="/dashboard-highlights/:id"
                    element={<DashboardHighlightEditorRoute />}
                  />
                  <Route path="/users" element={<UsersRoute />} />
                  <Route path="/users/:id" element={<UserEditorRoute />} />
                  <Route path="/chat" element={<ChatRoute />} />
                  <Route path="/analytics" element={<AnalyticsRoute />} />
                  <Route path="/chat/:threadId" element={<ChatRoute />} />
                </Route>
                <Route path="*" element={<Navigate to="/articles" replace />} />
              </Routes>
            </AuthGate>
          </Router>
        </QueryClientProvider>
      </I18nProvider>
    </ThemeProvider>
  );
}
